/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, List, ListItem, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';

import {
  NoticeAttachmentType,
  StatusCode,
} from '../../../../../../api/enumerations';
import {
  createPublicNoticeAttachment,
  deletePublicNoticeAttachment,
  listPublicNoticeAttachment,
} from '../../../../../../api/theHive/notices';
import { NoticeAttachmentData } from '../../../../../../api/theHive/notices/types';
import { CustomTextField } from '../../../../../../components/CustomInput';
import { ButtonBox } from '../../../../../../components/UI/Box';
import { WhiteCircularProgress } from '../../../../../../components/UI/Typography';
import { GlobalContext } from '../../../../../../context/global';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import { TableSection } from '../../NoticeDetails/styles';
import ConfirmationDeleteDialog from '../ConfirmationDeleteDialog';
import {
  DialogContainer,
  FilledButton,
  OutlinedButton,
  StyledDialog,
} from './styles';

interface LinksModalProps {
  noticeId: number;
}

export function LinksModal({ noticeId }: LinksModalProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [link, setLink] = useState('');
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [linksList, setLinksList] = useState<NoticeAttachmentData[]>([]);

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();

  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => {
    setLinkName('');
    setLink('');
    setOpen(false);
  };

  const checkUrl = (link: string): string => {
    return link.startsWith('http') ? link : `https://${link}`;
  };

  const handleLinks = async (): Promise<void> => {
    try {
      const response = await listPublicNoticeAttachment(
        noticeId,
        1,
        100,
        'link'
      );

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      setLinksList(response.data);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  useEffect(() => {
    if (noticeId) handleLinks();
  }, [noticeId]);

  const handleSubmit = async (): Promise<void> => {
    if (link.length < 1 || linkName.length < 1) {
      setSnackbarMessage('Preencha link e nome do link');
      setErrorMessage(true);
      setOpenSnackbar(true);
      return;
    }

    setLoadingSubmit(true);

    const formData = new FormData();
    formData.append('description', linkName);
    formData.append('link', link);
    formData.append('attachment_type', NoticeAttachmentType.LINK.toString());

    try {
      const response = await createPublicNoticeAttachment(noticeId, formData);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      setSnackbarMessage('Link adicionado com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);

      handleLinks();
      handleClose();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleDeleteLink = async (linkId: number): Promise<void> => {
    try {
      const response = await deletePublicNoticeAttachment(linkId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      setSnackbarMessage('Link deletado!');
      setErrorMessage(false);
      setOpenSnackbar(true);

      handleLinks();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  return (
    <TableSection>
      <Box display="flex" justifyContent="space-between">
        <Typography fontSize={21}>Links relacionados</Typography>
        <Button onClick={handleOpen}>adicionar links relacionados</Button>
        <StyledDialog open={open} onClose={handleClose}>
          <DialogContainer>
            <Typography fontWeight={600}>Adicionar link</Typography>
            <CustomTextField
              required
              id="link-name"
              label="nome do link"
              value={linkName}
              setValue={setLinkName}
              maxLength={60}
            />
            <CustomTextField
              required
              id="link"
              label="link"
              value={link}
              setValue={setLink}
              maxLength={200}
            />
            <ButtonBox gap={2} my={2.5}>
              <FilledButton onClick={handleSubmit} disabled={loadingSubmit}>
                {loadingSubmit ? <WhiteCircularProgress size={22} /> : 'enviar'}
              </FilledButton>
              <OutlinedButton onClick={handleClose} disabled={loadingSubmit}>
                cancelar
              </OutlinedButton>
            </ButtonBox>
          </DialogContainer>
        </StyledDialog>
      </Box>
      <List sx={{ listStyleType: 'disc' }}>
        {linksList.length > 0 ? (
          linksList.map((link) => (
            <ListItem sx={{ display: 'list-item', pl: 1 }} key={link.id}>
              {link.link && (
                <Box>
                  <a
                    href={checkUrl(link.link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit' }}
                  >
                    {link.description}
                  </a>
                  <ConfirmationDeleteDialog
                    iconDelete={false}
                    text="excluir este link"
                    modalCallback={() => handleDeleteLink(link.id)}
                  />
                </Box>
              )}
            </ListItem>
          ))
        ) : (
          <Typography>Nenhum link foi adicionado</Typography>
        )}
      </List>
    </TableSection>
  );
}
